import * as d3 from 'd3';
import {
    BUILDER_UNKNOWN,
    BUILDER_VANILLA,
    SLOT_TIME,
} from '../utils/constants.js';

export default class BlockValueUI {
    static SPACE_INTERVAL = 1000

    constructor(element) {
        this.padding = { top: 10, right: 50, bottom: 40, left: 60 };
        var parentDiv = document.querySelector(element); // Replace with the ID of your parent div
        var rect = parentDiv.getBoundingClientRect();
        this.height = rect.height;
        this.width = rect.width;
        this.format4 = d3.format('.4f');

        this.svg = d3.select(element).append("svg")
            .attr("width", this.width)
            .attr("height", this.height)

        this.x = d3.scaleLinear()
            .domain([0, SLOT_TIME + 1])
            .range([this.padding.left, this.width - this.padding.right]);

        this.y = d3.scaleLinear()
            .domain([0, 0.1])
            .range([this.height - this.padding.bottom, this.padding.top]);

        this.xaxis = this.svg.append("g")
            .attr("transform", `translate(0, ${this.height - this.padding.bottom})`)
            .call(d3.axisBottom(this.x));

        this.yaxis = this.svg.append("g")
            .attr("transform", `translate(${this.padding.left}, 0)`)
            .call(d3.axisLeft(this.y));

        // X label
        this.xlabel = this.svg.append('text')
            .attr('x', this.width / 2)
            .attr('y', this.height)
            .attr('text-anchor', 'middle')
            .style('font-family', 'Courier,Courier New,slab-serif')
            .style('font-size', 12)
            .style("font-weight", "bold")
            .style('fill', '#d3d3d3')
            .text('Slot Time (sec)');

        // Y label
        this.ylabel = this.svg.append('text')
            .attr('text-anchor', 'middle')
            .attr('transform', 'translate(12,' + (this.height - this.padding.bottom + this.padding.top) / 2 + ')rotate(-90)')
            .style('font-family', 'Courier,Courier New,slab-serif')
            .style('font-size', 12)
            .style("font-weight", "bold")
            .style('fill', '#d3d3d3')
            .text('Block Value');

        this.fline = this.svg.append('line')
            .attr('x1', this.x(SLOT_TIME))
            .attr('x2', this.x(SLOT_TIME))
            .attr('y1', this.padding.top)
            .attr('y2', this.height - this.padding.bottom)
            .attr('stroke-dasharray', '2,2')
            .attr('stroke', 'gray')
            .attr('opacity', 0.7);

        this.setupValueToolTip(element)
        // data
        this.bid_summaries = []
        this.next_summaries = []
    }

    setBlockBidUI = (block_bid_ui) => {
        this.block_bid_ui = block_bid_ui
    }

    setProgressUI = (progress_ui) => {
        this.progress_ui = progress_ui
    }

    setBlockInfo = (block_info) => {
        if (block_info.submissions !== undefined && block_info.submissions.length > 0) {
            // prepare
            const data = [];
            block_info.submissions.forEach(summary => {
                let x = summary.timestamp - summary.block_timestamp // timestamp are in millisecond precision
                if (x > SLOT_TIME + 1) {
                    x = SLOT_TIME + 1
                }
                data.push({ x: x, y: summary.best.value, builder: summary.best.builder, num_tx: summary.best.num_tx, gas_used: summary.best.gas_used, best_by_builder: summary.best_by_builder, bids_by_relay: summary.bids_by_relay });
            });

            this.y.domain([0, data[data.length - 1].y * 1.5]);
            if (this.yaxis !== undefined) {
                this.yaxis.remove()
            }
            this.yaxis = this.svg.append("g")
                .attr("transform", `translate(${this.padding.left}, 0)`)
                .call(d3.axisLeft(this.y));

            // update line
            this.drawLine(data);
            // update dots
            this.drawDots(data);
            // draw best
            if (block_info.best.timestamp > 0) {
                let x1 = (block_info.best.timestamp_ms - (block_info.timestamp - 12) * 1000) / 1000
                if (x1 > SLOT_TIME + 1) {
                    x1 = SLOT_TIME + 1
                }
                this.drawBest({
                    x: x1,
                    y: block_info.best.value,
                    builder: block_info.best.builder,
                })
            }
            if (block_info.winner.builder_pubkey !== "") {
                let x1 = (block_info.winner.timestamp_ms - (block_info.timestamp - 12) * 1000) / 1000
                if (x1 > SLOT_TIME + 1) {
                    x1 = SLOT_TIME + 1
                }
                this.drawWinner({
                    x: x1,
                    y: block_info.winner.value,
                    builder: block_info.winner.builder,
                })
            }
            if (block_info.builder === BUILDER_VANILLA || (block_info.offset === 0 && block_info.builder.includes("geth"))) {
                let x1 = (block_info.winner.timestamp_ms - (block_info.timestamp - 12) * 1000) / 1000
                if (x1 > SLOT_TIME + 1) {
                    x1 = SLOT_TIME + 1
                }
                this.drawPrivate({
                    x: -1,
                    y: block_info.payment,
                })
            }
        }
    }

    newBidSummary = (bid_summary) => {
        //console.log(bid_summary)
        if (this.bid_summaries.length > 0 && this.bid_summaries[0].block > bid_summary.block) {
            // old bid summary
            return
        }
        // push new summary
        if (this.bid_summaries.length === 0 || bid_summary.best.timestamp - this.bid_summaries[this.bid_summaries.length - 1].best.timestamp >= BlockValueUI.SPACE_INTERVAL) {
            if (this.block_summary !== undefined && bid_summary.block > this.block_summary.block) {
                this.bid_summaries.push(bid_summary)
                if (this.bid_summaries.length > 1) {
                    this.clearWinner()
                    this.updateUi()
                }
            }
        }
    }

    newBlockSummary = (block_summary) => {
        // TODO: if past block bids were empty and new block summary has no winner
        if (this.block_summary === undefined || this.block_summary.block < block_summary.block) {
            this.clearWinner()
            // draw the best and winner
            if (this.bid_summaries.length > 0 && block_summary.winner !== undefined && block_summary.best !== undefined) {
                // get best and winner data
                let data = this.prepareBlockSummary(block_summary)
                if (this.valueLabel !== undefined) {
                    this.valueLabel.remove()
                }
                if (block_summary.block > this.bid_summaries[0].block) {
                    if (this.dots !== undefined) {
                        this.dots.remove()
                    }
                }
                this.drawBest(data[0])
                this.drawWinner(data[1])
                this.drawPrivate(data[2])
            }
            // set new block_summary
            this.block_summary = block_summary
            // clear current bid summaries
            this.bid_summaries = []
            // set timer to clear previous nonethlesse
            setTimeout(() => {
                if (this.bid_summaries.length === 0) {
                    if (this.valueLabel !== undefined) {
                        this.valueLabel.remove()
                    }
                    if (this.dots !== undefined) {
                        this.dots.remove()
                    }
                    if (this.line !== undefined) {
                        this.line.remove()
                    }
                    this.clearWinner()
                }
            }, 4000);
        }
    }

    setupValueToolTip = (block_value_div) => {
        // Add a tooltip div. Here I define the general feature of the tooltip: stuff that do not depend on the data point.
        // Its opacity is set to 0: we don't see it by default.
        this.tooltipValue = d3.select(block_value_div)
            .append("div")
            .style("opacity", 0)
            .attr("class", "tooltip")

        // A function that change this tooltip when the user hover a point.
        // Its opacity is set to 1: we can now see it. Plus it set the text and position of tooltip depending on the datapoint (d)
        this.mouseoverValue = (e, d) => {
            this.tooltipValue = this.tooltipValue
                .style("opacity", 1)
        }

        this.mousemoveValue = (e, data) => {
            const [x, y] = d3.pointer(e);

            // Calculate the tooltip position relative to the mouse cursor
            const tooltipLeft = x + 10; // Add an offset to adjust the position
            const tooltipTop = y + 10; // Add an offset to adjust the position

            var builder = data.builder
            if (builder === "" || builder === BUILDER_UNKNOWN) {
                builder = "unknown"
            }

            this.tooltipValue = this.tooltipValue
                .html(`
                <div class='tooltip-tx'>
                    <strong style="display: inline-block; width: 80px;">Builder:</strong> ${builder}<br>
                    <strong style="display: inline-block; width: 80px;">Value:</strong> ${data.y}<br>
                </div>
                `)
                .style("left", `${tooltipLeft}px`) // It is important to put the +90: other wise the tooltip is exactly where the point is an it creates a weird effect
                .style("top", `${tooltipTop - this.height}px`)
        }

        // A function that change this tooltip when the leaves a point: just need to set opacity to 0 again
        this.mouseleaveValue = (e, d) => {
            this.tooltipValue = this.tooltipValue
                .html("")
                .style("left", `0px`)
                .style("top", `0px`)
                .style("opacity", 0)
        }

        this.clickValue = (e, d) => {
            this.block_bid_ui?.setBuilderMap(d.best_by_builder, d.bids_by_relay)
            this.progress_ui?.setProgress(d.x)
        }
    }

    updateUi = () => {
        if (this.bid_summaries.length === 0) {
            return
        }
        // prepare bid summaries
        var data = this.prepareBidSummaries()
        // expand axis domain according to data
        // expand axis domain to 120% of best value
        if (data.length === 2 || data[data.length - 1].y * 1.2 > this.y.domain()[1]) {
            if (data.length === 2) {
                var maxY = data[data.length - 1].y * 5
            } else {
                var maxY = data[data.length - 1].y * 1.5
            }
            this.y.domain([0, maxY]);
            if (this.yaxis !== undefined) {
                this.yaxis.remove()
            }
            this.yaxis = this.svg.append("g")
                .attr("transform", `translate(${this.padding.left}, 0)`)
                .call(d3.axisLeft(this.y));

            this.lastY = maxY
        }
        // update line
        this.drawLine(data);
        // update dots
        this.drawDots(data);
    }

    prepareBidSummaries() {
        const data = [];
        this.bid_summaries.forEach(summary => {
            let x = (summary.best.timestamp - summary.block_timestamp / 1e6) / 1000 // timestamp are in millisecond precision
            if (x > SLOT_TIME + 1) {
                x = SLOT_TIME + 1
            }
            data.push({ x: x, y: summary.best.value, builder: summary.best.builder, num_tx: summary.best.num_tx, gas_used: summary.best.gas_used, best_by_builder: summary.best_by_builder });
        });
        return data;
    }

    prepareBlockSummary(block_summary) {
        const data = [];

        // add best
        let x1 = (block_summary.best.timestamp_ms - this.block_summary.timestamp / 1e6) / 1000
        if (x1 > SLOT_TIME + 1) {
            x1 = SLOT_TIME + 1
        }
        data.push({ x: x1, y: block_summary.best.value / 1e18, builder: block_summary.best.builder, num_tx: block_summary.best.num_tx });

        // add winner
        let x2 = (block_summary.winner.timestamp_ms - this.block_summary.timestamp / 1e6) / 1000
        if (x2 > SLOT_TIME + 1) {
            x2 = SLOT_TIME + 1
        }
        data.push({ x: x2, y: block_summary.winner.value / 1e18, builder: block_summary.winner.builder, num_tx: block_summary.winner.num_tx });

        // add private
        var y = block_summary.winner.value / 1e18
        if (y === 0 || block_summary.payment > 0) {
            y = block_summary.payment
        }
        data.push({ x: x2, y: y, builder: block_summary.winner.builder, num_tx: block_summary.winner.num_tx });

        return data;
    }

    drawYAxis = (data) => {
        // expand axis domain to 120% of best value
        if (data.length === 1 || data[data.length - 1].y * 1.2 > this.y.domain()[1]) {
            if (data.length === 1) {
                var maxY = data[data.length - 1].y * 5
            } else {
                var maxY = data[data.length - 1].y * 1.5
            }
            this.y.domain([0, maxY]);
            if (this.yaxis !== undefined) {
                this.yaxis.remove()
            }
            this.yaxis = this.svg.append("g")
                .attr("transform", `translate(${this.padding.left}, 0)`)
                .call(d3.axisLeft(this.y));

            this.lastY = maxY
        }
    }

    drawDots = (data) => {
        if (this.dots !== undefined) {
            this.dots.remove()
        }
        if (this.valueLabel !== undefined) {
            this.valueLabel.remove()
        }

        this.dots = this.svg.append("g")
            .attr("fill", "transparent")
            .attr("stroke", "#d3d3d3")
            .attr("stroke-width", 2)
            .style("cursor", "pointer")
            .selectAll("circle")
            .data(data)
            .join("circle")
            .attr("cx", d => this.x(d.x))
            .attr("cy", d => this.y(d.y))
            .attr("r", 3)
            .style("pointer-events", "all") // Set pointer-events to "all" to capture events on transparent areas
            .on("mouseover", this.mouseoverValue)
            .on("mousemove", (event, d) => {
                this.mousemoveValue(event, d);
            })
            .on("mouseleave", this.mouseleaveValue)
            .on("click", (event, d) => {
                this.clickValue(event, d);
            })

        this.valueLabel = this.svg.selectAll('.label')
            .data([data[data.length - 1]])
            .enter()
            .append('text')
            .attr('class', 'label')
            .attr('x', d => this.x(d.x))
            .attr('y', d => this.y(d.y) - 10)
            .attr('text-anchor', 'middle')
            .style("font-family", "Courier,Courier New,slab-serif")
            .style("font-weight", "bold")
            .style('font-size', '14px')
            .style('fill', '#d3d3d3')
            .text(d => `${this.format4(d.y)} ETH`);
    }

    drawLine = (data) => {
        if (this.line !== undefined) {
            this.line.remove()
        }
        this.line = this.svg.append("path")
            .datum(data)
            .attr("fill", "none")
            .attr("stroke", "#d3d3d3")
            .attr("stroke-width", 1)
            .attr("d", d3.line()
                .curve(d3.curveStepAfter) // Just add that to have a curve instead of segments
                .x(d => this.x(d.x))
                .y(d => this.y(d.y))
            )
    }

    drawWinner = (data) => {
        if (data.x > 0) {
            this.winner = this.svg.append("g")
                .attr("fill", "none")
                .attr("stroke", "#26a6ed")
                .attr("stroke-width", 2)
                .selectAll("circle")
                .data([data])
                .join("circle")
                .attr("cx", d => this.x(d.x))
                .attr("cy", d => this.y(d.y))
                .attr("r", 5);

            this.winnerLabel = this.svg.selectAll('.winner-label')
                .data([data])
                .enter()
                .append('text')
                .attr('class', 'label')
                .attr('x', d => this.width / 2)
                .attr('y', d => this.y(d.y) - 5)
                .attr('text-anchor', 'middle')
                .style("font-family", "Courier,Courier New,slab-serif")
                .style('font-size', '14px')
                .style('fill', '#26a6ed')
                .text(d => `${this.format4(d.y)} ETH`);

            // Define starting and ending points
            const startPoint = { x: this.padding.left, y: this.y(data.y) };
            const endPoint = { x: this.width - this.padding.right, y: this.y(data.y) };

            this.winnerLineX = this.svg.append('line')
                .attr('x1', startPoint.x)
                .attr('y1', startPoint.y)
                .attr('x2', endPoint.x)
                .attr('y2', endPoint.y)
                .attr('stroke-dasharray', '2,2')
                .attr('stroke', '#26a6ed');
        }
    }

    drawBest = (data) => {
        this.best = this.svg.append("g")
            .attr("fill", "none")
            .attr("stroke", "#f9f871")
            .attr("stroke-width", 2)
            .selectAll("circle")
            .data([data])
            .join("circle")
            .attr("cx", d => this.x(d.x))
            .attr("cy", d => this.y(d.y))
            .attr("r", 5);

        this.bestLabel = this.svg.selectAll('.best-label')
            .data([data])
            .enter()
            .append('text')
            .attr('class', 'label')
            .attr('x', d => this.x(d.x) - 40)
            .attr('y', d => this.y(d.y) - 5)
            .attr('text-anchor', 'middle')
            .style("font-family", "Courier,Courier New,slab-serif")
            .style('font-size', '14px')
            .style('fill', '#f9f871')
            .text(d => `${this.format4(d.y)} ETH`);

        // Define starting and ending points
        const startPoint = { x: this.padding.left, y: this.y(data.y) };
        const endPoint = { x: this.width - this.padding.right, y: this.y(data.y) };

        this.bestLineX = this.svg.append('line')
            .attr('x1', startPoint.x)
            .attr('y1', startPoint.y)
            .attr('x2', endPoint.x)
            .attr('y2', endPoint.y)
            .attr('stroke-dasharray', '2,2')
            .attr('stroke', '#f9f871');
    }

    drawPrivate = (data) => {
        if (data.x < 0) {
            this.privateLabel = this.svg.selectAll('.private-label')
                .data([data])
                .enter()
                .append('text')
                .attr('class', 'label')
                .attr('x', d => this.width / 2)
                .attr('y', d => this.y(d.y) - 5)
                .attr('text-anchor', 'middle')
                .style("font-family", "Courier,Courier New,slab-serif")
                .style('font-size', '14px')
                .style('fill', '#fb717f')
                .text(d => `${this.format4(d.y)} ETH`);

            // Define starting and ending points
            const startPoint = { x: this.padding.left, y: this.y(data.y) };
            const endPoint = { x: this.width - this.padding.right, y: this.y(data.y) };

            this.privateLineX = this.svg.append('line')
                .attr('x1', startPoint.x)
                .attr('y1', startPoint.y)
                .attr('x2', endPoint.x)
                .attr('y2', endPoint.y)
                .attr('stroke-dasharray', '2,2')
                .attr('stroke', '#fb717f');
        }
    }

    clearWinner = () => {
        // clear winner and best
        if (this.winner !== undefined) {
            this.winner.remove()
        }
        if (this.winnerLabel !== undefined) {
            this.winnerLabel.remove()
        }
        if (this.winnerLineX !== undefined) {
            this.winnerLineX.remove()
        }
        if (this.best !== undefined) {
            this.best.remove()
        }
        if (this.bestLabel !== undefined) {
            this.bestLabel.remove()
        }
        if (this.bestLineX !== undefined) {
            this.bestLineX.remove()
        }
        if (this.privateLabel !== undefined) {
            this.privateLabel.remove()
        }
        if (this.privateLineX !== undefined) {
            this.privateLineX.remove()
        }
    }

}
